import React, { useState, useEffect } from 'react';
import { Users, UserCheck, Building, Activity, TrendingUp, TrendingDown, DollarSign, AlertTriangle, CheckSquare, Clock, User, Calendar } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import AdminSubRoleWork from './AdminSubRoleWork';

const SUBROLES = ['doctor_verifier', 'finance_admin', 'moderator', 'support_admin'];

export default function AdminDashboard() {
  const storedUser = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
  const isSubRole = storedUser && SUBROLES.includes(storedUser.role);
  const [metrics, setMetrics] = useState(null);
  const [myTasks, setMyTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isSubRole) { setLoading(false); return; }
    fetchDashboardMetrics();
    fetchMyTasks();
  }, [isSubRole]);

  const fetchDashboardMetrics = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard/metrics`, {
        headers: { }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch metrics');
      }

      const data = await response.json();
      setMetrics(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyTasks = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/my-tasks`, {
        headers: { }
      });

      if (response.ok) {
        const data = await response.json();
        setMyTasks(data.tasks || []);
      }
    } catch (err) {
    }
  };

  const handleTaskStatusUpdate = async (taskId, newStatus) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/tasks/${taskId}/status`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        fetchMyTasks(); // Refresh tasks
      }
    } catch (err) {
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800'
    };
    return styles[status] || styles.pending;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return styles[priority] || styles.medium;
  };

  if (isSubRole) {
    return <AdminSubRoleWork userName={storedUser?.name} />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-6xl mb-4">⏳</div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard Overview</h1>
          <p className="text-gray-600 mt-1">Monitor platform health and key metrics</p>
        </div>
        <Badge className="bg-green-100 text-green-800 px-4 py-2">
          System Status: Online
        </Badge>
      </div>

      {/* Platform Health Metrics */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Platform Health</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-2 border-blue-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <Users className="w-8 h-8 text-blue-600" />
                <Badge variant="outline" className="text-blue-600">Live</Badge>
              </div>
              <p className="text-3xl font-bold text-gray-900">{metrics?.platform_health?.total_patients || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Total Patients</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-teal-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <UserCheck className="w-8 h-8 text-teal-600" />
                <Badge variant="outline" className="text-teal-600">Active</Badge>
              </div>
              <p className="text-3xl font-bold text-gray-900">{metrics?.platform_health?.total_doctors || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Verified Doctors</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-orange-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <Building className="w-8 h-8 text-orange-600" />
                <Badge variant="outline" className="text-orange-600">Partners</Badge>
              </div>
              <p className="text-3xl font-bold text-gray-900">{metrics?.platform_health?.total_hospitals || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Hospital Partners</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-purple-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <Activity className="w-8 h-8 text-purple-600" />
                <Badge variant="outline" className="text-purple-600">In Progress</Badge>
              </div>
              <p className="text-3xl font-bold text-gray-900">{metrics?.platform_health?.active_treatments || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Active Treatments</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Patient Metrics */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Patient Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">New Patients (30d)</p>
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{metrics?.patient_metrics?.new_30d || 0}</p>
              <p className="text-xs text-green-600 mt-1">+12.5% from last month</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Conversion Rate</p>
                <Badge className="bg-green-100 text-green-800">{metrics?.patient_metrics?.conversion_rate || 0}%</Badge>
              </div>
              <p className="text-2xl font-bold text-gray-900">{metrics?.patient_metrics?.conversion_rate || 0}%</p>
              <p className="text-xs text-gray-600 mt-1">Treatment requests</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Satisfaction Score</p>
                <Badge className="bg-yellow-100 text-yellow-800">★ {metrics?.patient_metrics?.satisfaction || 0}</Badge>
              </div>
              <p className="text-2xl font-bold text-gray-900">{metrics?.patient_metrics?.satisfaction || 0}/5.0</p>
              <p className="text-xs text-gray-600 mt-1">Average rating</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Churn Rate</p>
                <TrendingDown className="w-5 h-5 text-red-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">2.3%</p>
              <p className="text-xs text-red-600 mt-1">Down 0.5% (good)</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Doctor Metrics */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Doctor Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-2 border-yellow-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Pending Verifications</p>
                <Badge className="bg-yellow-100 text-yellow-800">Action Required</Badge>
              </div>
              <p className="text-2xl font-bold text-gray-900">{metrics?.doctor_metrics?.pending_verifications || 0}</p>
              <p className="text-xs text-gray-600 mt-1">Awaiting review</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Average Rating</p>
                <Badge className="bg-green-100 text-green-800">★ {metrics?.doctor_metrics?.avg_rating || 0}</Badge>
              </div>
              <p className="text-2xl font-bold text-gray-900">{metrics?.doctor_metrics?.avg_rating || 0}/5.0</p>
              <p className="text-xs text-gray-600 mt-1">Platform average</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Availability Rate</p>
                <Badge className="bg-blue-100 text-blue-800">{metrics?.doctor_metrics?.availability_rate || 0}%</Badge>
              </div>
              <p className="text-2xl font-bold text-gray-900">{metrics?.doctor_metrics?.availability_rate || 0}%</p>
              <p className="text-xs text-gray-600 mt-1">Online doctors</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Response Time</p>
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">2.4h</p>
              <p className="text-xs text-green-600 mt-1">-15% from last week</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Financial Metrics */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Financial Overview (Last 30 Days)</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-2 border-green-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-8 h-8 text-green-600" />
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">
                ${(metrics?.financial_metrics?.total_revenue_30d || 0).toLocaleString()}
              </p>
              <p className="text-sm text-gray-600 mt-1">Total Revenue</p>
              <p className="text-xs text-green-600 mt-1">+18.2% vs last month</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Commission Earned</p>
                <Badge className="bg-purple-100 text-purple-800">15%</Badge>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                ${(metrics?.financial_metrics?.commission_earned || 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-600 mt-1">Platform earnings</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-red-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Failed Payments</p>
                <Badge className="bg-red-100 text-red-800">Alert</Badge>
              </div>
              <p className="text-2xl font-bold text-gray-900">{metrics?.financial_metrics?.failed_payments || 0}</p>
              <p className="text-xs text-red-600 mt-1">Requires attention</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-orange-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Open Disputes</p>
                <Badge className="bg-orange-100 text-orange-800">Review</Badge>
              </div>
              <p className="text-2xl font-bold text-gray-900">{metrics?.financial_metrics?.open_disputes || 0}</p>
              <p className="text-xs text-gray-600 mt-1">Pending resolution</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* System Health */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">System Health</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">System Uptime</p>
                <Badge className="bg-green-100 text-green-800">Excellent</Badge>
              </div>
              <p className="text-2xl font-bold text-gray-900">{metrics?.platform_health?.system_uptime || 0}%</p>
              <p className="text-xs text-gray-600 mt-1">Last 30 days</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">API Response Time</p>
                <Badge className="bg-blue-100 text-blue-800">Good</Badge>
              </div>
              <p className="text-2xl font-bold text-gray-900">{metrics?.platform_health?.api_response_time || 0}ms</p>
              <p className="text-xs text-gray-600 mt-1">Average latency</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Active Sessions</p>
                <Badge className="bg-purple-100 text-purple-800">Live</Badge>
              </div>
              <p className="text-2xl font-bold text-gray-900">1,247</p>
              <p className="text-xs text-gray-600 mt-1">Current users online</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* My Tasks Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">My Tasks</h2>
          <Badge className="bg-blue-100 text-blue-800">
            {myTasks.filter(t => t.status !== 'completed').length} Active
          </Badge>
        </div>
        
        {myTasks.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <CheckSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No tasks assigned to you yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {myTasks.slice(0, 5).map((task) => (
              <Card key={task._id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-gray-900">{task.title}</h4>
                        <Badge className={getStatusBadge(task.status)}>
                          {task.status}
                        </Badge>
                        <Badge className={getPriorityBadge(task.priority)}>
                          {task.priority}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-3">{task.description}</p>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          <span>Patient: {task.patient_name || 'N/A'}</span>
                        </div>
                        {task.due_date && (
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {task.status === 'pending' && (
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => handleTaskStatusUpdate(task._id, 'in_progress')}
                        >
                          Start
                        </Button>
                      )}
                      {task.status === 'in_progress' && (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handleTaskStatusUpdate(task._id, 'completed')}
                        >
                          Complete
                        </Button>
                      )}
                      {task.status === 'completed' && (
                        <Badge className="bg-green-100 text-green-800">
                          ✓ Done
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {myTasks.length > 5 && (
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-gray-600">
                    + {myTasks.length - 5} more tasks
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
